import {
  Component,
  Events,
  ICellRendererComp,
  ICellRendererParams,
  RefSelector,
} from '@ag-grid-community/core'
import { booleanInputSetter } from '@northfork/basics'

/** @internal */
const template = `
  <div class="editable-boolean-renderer custom-control custom-checkbox">
    <input ref="checkbox-input" type="checkbox" class="custom-control-input" />
    <label ref="checkbox-label" class="custom-control-label"></label>
  </div>
  `

export class EditableCheckboxRenderer
  extends Component
  implements ICellRendererComp
{
  private disabled = false

  @RefSelector('checkbox-input')
  private readonly checkboxInput?: HTMLInputElement
  @RefSelector('checkbox-label')
  private readonly checkboxLabel?: HTMLLabelElement

  private params?: ICellRendererParams
  private readonly valueChangeEvent = () => {
    this.setDisabled(this.params)
  }

  constructor() {
    super(template)
  }

  init(params: ICellRendererParams) {
    this.params = params
    this.refresh(params)
    this.attachClick(params)
    params.api.addEventListener(
      Events.EVENT_CELL_VALUE_CHANGED,
      this.valueChangeEvent,
    )
  }

  destroy() {
    this.params?.api.removeEventListener(
      Events.EVENT_CELL_VALUE_CHANGED,
      this.valueChangeEvent,
    )
  }

  refresh(params: ICellRendererParams): boolean {
    this.setId(params)
    this.setDisabled(params)
    this.setChecked(params)
    return true
  }

  private setDisabled(params?: ICellRendererParams) {
    this.disabled = params == null || !this.isEditable(params)
    if (this.checkboxInput != null) {
      this.checkboxInput.disabled = this.disabled
    }
  }

  private setId(params: ICellRendererParams) {
    if (
      this.checkboxInput != null &&
      this.checkboxLabel != null &&
      params.column != null
    ) {
      const baseId = `checkbox-${params.node.id}-${params.column.getColId()}`
      const id = `${baseId}-${Math.random()}`

      this.checkboxInput.setAttribute('id', id)
      this.checkboxLabel.setAttribute('for', id)
    }
  }

  private setChecked(params: ICellRendererParams) {
    if (this.checkboxInput != null && params.getValue != null) {
      this.checkboxInput.checked = booleanInputSetter(params.getValue())
    }
  }

  private attachClick(params: ICellRendererParams) {
    if (this.checkboxInput == null) {
      return
    }

    this.checkboxInput.addEventListener('click', () => {
      this.applyClick(params)
    })
  }

  applyClick(params: ICellRendererParams) {
    if (this.disabled === false && this.checkboxInput != null) {
      const targetState = this.checkboxInput.checked
      this.checkboxInput.checked = !targetState

      if (params.column != null) {
        params.node.setDataValue(params.column, targetState)
      }
    }
  }

  private isEditable(params: ICellRendererParams) {
    if (params.colDef != null && params.colDef.editable == null) {
      params.colDef.editable = true
    }

    return params.column?.isCellEditable(params.node) ?? true
  }
}
